// @givmo/board — add one entry to a draft packet version.
//
// FOUR KINDS, and each one names a DIFFERENT target rather than carrying its own content:
//
//   * `agenda_item` — a pointer at an item already on this meeting's agenda. The packet says "this is
//     the paper for item 3", so it never re-types the agenda.
//   * `document` — a pointer at a file already attached to this MEETING (files outlive versions, so
//     the picker offers what is already there rather than an upload inside a version).
//   * `board_record` — a pointer into the minute book. Only records the officer may READ can be
//     placed, and the backend enforces that on the way in: without it, an admin who was not in a closed
//     session could hand the whole board a pointer to content the record store withholds from them.
//     Only APPROVED records are offered here — pointing a board at somebody's unfinished draft is a
//     different act, and not one a packet performs.
//   * `note` — the officer's own words, which is also the sanctioned way for an officer to put a
//     comment into a packet at all. There is no private-note path for staff, deliberately.
//
// Position is left to the backend: entries append, and order is set afterwards by the whole-list
// reorder (its constraint is deferred precisely so a swap is possible at all).

import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';

import { extractList } from '@givmo/api-client/list';
import {
  isGivmoError,
  type BoardAgendaItemOut,
  type BoardPacketItemKind,
  type OrgBoardRecordOut,
} from '@givmo/api-client';
import {
  Button,
  Dialog,
  ErrorMessage,
  Select,
  TextField,
  Textarea,
  toast,
} from '@givmo/design-system';
import { FieldNote } from '../../components/field-note';
import { extractDocuments, isUploaded } from './packets-decode';
import { useBoardOperations } from '../../board-runtime';
import { committeeMeetingsWords as cm } from '../committees/committee-meetings-words';

interface KindOption {
  value: BoardPacketItemKind;
  label: string;
}

const KIND_OPTIONS: readonly KindOption[] = [
  { value: 'agenda_item', label: 'Something on the agenda' },
  { value: 'document', label: 'A file attached to this meeting' },
  { value: 'board_record', label: 'A record from the minute book' },
  { value: 'note', label: 'A note you write here' },
];

export interface PacketEntryDialogProps {
  orgRef: string;
  meetingId: string;
  packetId: string;
  /** Hides the attachment kind when this environment has no document storage configured. */
  attachmentsAvailable: boolean;
  /** CBG-YR6-MEET: a committee meeting's packet is read by the committee's members. */
  committeeName?: string | null;
  onClose: () => void;
  onAdded: () => void;
}

export function PacketEntryDialog({
  orgRef,
  meetingId,
  packetId,
  attachmentsAvailable,
  committeeName = null,
  onClose,
  onAdded,
}: PacketEntryDialogProps) {
  const operations = useBoardOperations();
  const [kind, setKind] = useState<BoardPacketItemKind>('agenda_item');
  const [agendaItemId, setAgendaItemId] = useState<string | null>(null);
  const [documentId, setDocumentId] = useState<string | null>(null);
  const [recordId, setRecordId] = useState<string | null>(null);
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [error, setError] = useState<string | null>(null);

  const agendaQuery = useQuery({
    ...operations.officerListBoardAgendaItemsOptions({
      path: { org_ref: orgRef, meeting_id: meetingId },
    }),
    select: (data: unknown) =>
      [...extractList<BoardAgendaItemOut>(data, 'data')].sort(
        (left, right) => left.position - right.position,
      ),
    enabled: kind === 'agenda_item',
  });

  const documentsQuery = useQuery({
    ...operations.officerListBoardDocumentsOptions({
      path: { org_ref: orgRef, meeting_id: meetingId },
    }),
    select: extractDocuments,
    enabled: kind === 'document',
  });

  const recordsQuery = useQuery({
    ...operations.officerListBoardRecordsOptions({
      path: { org_ref: orgRef },
      query: { state: 'approved' },
    }),
    select: (data: unknown) => extractList<OrgBoardRecordOut>(data, 'data'),
    enabled: kind === 'board_record',
  });

  const agendaItems = agendaQuery.data ?? [];
  // Only a file whose bytes actually landed can be handed to the board.
  const attachments = (documentsQuery.data ?? []).filter(isUploaded);
  const records = recordsQuery.data ?? [];

  const kindOptions = KIND_OPTIONS.filter(
    (option) => option.value !== 'document' || attachmentsAvailable,
  );

  const chosenTarget =
    kind === 'agenda_item'
      ? agendaItemId
      : kind === 'document'
        ? documentId
        : kind === 'board_record'
          ? recordId
          : null;
  const canSave =
    kind === 'note' ? title.trim().length > 0 : chosenTarget !== null && chosenTarget.length > 0;

  const add = useMutation({
    mutationFn: async () => {
      await operations.officerCreateBoardPacketItem({
        path: { org_ref: orgRef, packet_id: packetId },
        body: {
          item_kind: kind,
          ...(kind === 'agenda_item' ? { agenda_item_id: agendaItemId } : {}),
          ...(kind === 'document' ? { document_id: documentId } : {}),
          ...(kind === 'board_record' ? { record_id: recordId } : {}),
          ...(kind === 'note'
            ? { title: title.trim(), body: body.trim().length > 0 ? body.trim() : null }
            : {}),
        },
        throwOnError: true,
      });
    },
    onSuccess: () => {
      toast.success('Added to the packet.');
      onAdded();
    },
    onError: (caught) => {
      setError(isGivmoError(caught) ? caught.message : 'We couldn’t add that. Please try again.');
    },
  });

  return (
    <Dialog.Root open onOpenChange={(next) => !next && !add.isPending && onClose()}>
      <Dialog.Content
        aria-label="Add something to the packet"
        className="flex flex-col gap-16"
        testId="packet-entry-dialog"
      >
        <Dialog.Title className="font-sans text-s18 font-w700 tracking-givmo text-text-strong">
          Add something to the packet
        </Dialog.Title>

        <Select
          label="What are you adding?"
          appearance="saas"
          value={kind}
          onValueChange={(next) => {
            if (next === null) return;
            setKind(next as BoardPacketItemKind);
            setError(null);
          }}
          options={kindOptions.map((option) => ({ value: option.value, label: option.label }))}
          testId="packet-entry-kind"
        />

        {kind === 'agenda_item' ? (
          <>
            <Select
              label="Which item"
              appearance="saas"
              value={agendaItemId ?? ''}
              onValueChange={(next) =>
                setAgendaItemId(next !== null && next.length > 0 ? next : null)
              }
              options={[
                { value: '', label: 'Choose an agenda item…' },
                ...agendaItems.map((item, index) => ({
                  value: item.id,
                  label: `${index + 1}. ${item.title}`,
                })),
              ]}
              testId="packet-entry-agenda-item"
            />
            {agendaQuery.isSuccess && agendaItems.length === 0 ? (
              <FieldNote>
                This meeting has no agenda yet. Build the agenda first and its items become
                available here.
              </FieldNote>
            ) : null}
          </>
        ) : null}

        {kind === 'document' ? (
          <>
            <Select
              label="Which file"
              appearance="saas"
              value={documentId ?? ''}
              onValueChange={(next) =>
                setDocumentId(next !== null && next.length > 0 ? next : null)
              }
              options={[
                { value: '', label: 'Choose a file…' },
                ...attachments.map((attachment) => ({
                  value: attachment.id,
                  label: attachment.file_name,
                })),
              ]}
              testId="packet-entry-document"
            />
            {documentsQuery.isSuccess && attachments.length === 0 ? (
              <FieldNote>
                Nothing is attached to this meeting yet. Attach a file above and it becomes
                available here.
              </FieldNote>
            ) : null}
          </>
        ) : null}

        {kind === 'board_record' ? (
          <>
            <Select
              label="Which record"
              appearance="saas"
              value={recordId ?? ''}
              onValueChange={(next) => setRecordId(next !== null && next.length > 0 ? next : null)}
              options={[
                { value: '', label: 'Choose a record…' },
                ...records.map((record) => ({ value: record.id, label: record.title })),
              ]}
              testId="packet-entry-record"
            />
            <FieldNote>
              Only records you can open are listed, and a director only sees the ones they can open
              too.
            </FieldNote>
          </>
        ) : null}

        {kind === 'note' ? (
          <>
            <TextField
              label="Heading"
              value={title}
              onChange={(event) => setTitle(event.target.value)}
              testId="packet-entry-note-title"
            />
            <Textarea
              label="What it says (optional)"
              value={body}
              onChange={(event) => setBody(event.target.value)}
              rows={4}
              testId="packet-entry-note-body"
            />
            <FieldNote>
              {committeeName !== null
                ? cm.packetNoteReaders(committeeName)
                : 'A note is packet contents — every director on this board reads it, and it stays in the version once you issue it.'}
            </FieldNote>
          </>
        ) : null}

        {error !== null ? <ErrorMessage testId="packet-entry-error">{error}</ErrorMessage> : null}

        <div className="flex justify-end gap-10">
          <Button
            appearance="saas"
            tone="secondary"
            disabled={add.isPending}
            onClick={onClose}
            testId="packet-entry-cancel"
          >
            Cancel
          </Button>
          <Button
            appearance="saas"
            tone="primary"
            loading={add.isPending}
            disabled={!canSave || add.isPending}
            onClick={() => {
              setError(null);
              add.mutate();
            }}
            testId="packet-entry-save"
          >
            Add it
          </Button>
        </div>
      </Dialog.Content>
    </Dialog.Root>
  );
}
